import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "./useAuth";

const APPLE_CAL_URL = "https://unnzlorzyswiahjlxwqh.supabase.co/functions/v1/apple-calendar-feed";
const ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InVubnpsb3J6eXN3aWFoamx4d3FoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzYxMDU5NTMsImV4cCI6MjA5MTY4MTk1M30.4zmLgxfgFlg9mnvkqQVPgsmV8bP1UO_OyLb3wXjgSfo";

export interface AppleCalEntry {
  id: string;
  calendar_name: string;
  created_at: string;
}

export interface AppleCalEvent {
  title: string;
  start: string;
  end?: string;
  location?: string;
  description?: string;
  all_day?: boolean;
}

interface AppleCalendarContextValue {
  connected: boolean;
  checking: boolean;
  calendars: AppleCalEntry[];
  checkStatus: () => Promise<void>;
  connect: (feedUrl: string) => Promise<{ success: boolean; calendar_name?: string; error?: string }>;
  disconnect: (feedId?: string) => Promise<void>;
  fetchEvents: (action: "get_today" | "get_week" | "get_month") => Promise<AppleCalEvent[]>;
  /** #1657 — an arbitrary window (ISO instants, ≤ 62 days) for the month grid
   *  and the strip. Empty on any failure: the DB rows still render. */
  fetchRange: (startIso: string, endIso: string) => Promise<AppleCalEvent[]>;
}

const AppleCalendarContext = createContext<AppleCalendarContextValue | null>(null);

export function AppleCalendarProvider({ children }: { children: ReactNode }) {
  const { user, session } = useAuth();
  const [connected, setConnected] = useState(false);
  const [checking, setChecking] = useState(true);
  const [calendars, setCalendars] = useState<AppleCalEntry[]>([]);

  /**
   * Every call carries HER access token, not the anon key (#492).
   *
   * This used to send `Bearer ${ANON_KEY}`. The Edge Function then trusted the
   * user_id in the body and queried with a service-role client — and the anon
   * key is public, it ships in this bundle. So anyone at all could read or
   * disconnect any user's calendar given their UUID. The function now derives
   * the acting user from this token, so it has to be a real session.
   *
   * Read from useAuth(), never supabase.auth.getSession() — see the June 29
   * lock-starvation note in CLAUDE.md.
   *
   * apikey stays the anon key: that header is Supabase's project router, not
   * an identity claim.
   */
  const post = useCallback(async (body: object) => {
    const token = session?.access_token;
    if (!token) {
      // No session: fail closed and locally. Firing an unauthenticated request
      // just to collect a 403 tells us nothing and logs noise server-side.
      return new Response(JSON.stringify({ error: "not_authenticated" }), {
        status: 401,
        headers: { "Content-Type": "application/json" },
      });
    }
    return fetch(APPLE_CAL_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        apikey: ANON_KEY,
      },
      body: JSON.stringify(body),
    });
  }, [session?.access_token]);

  const checkStatus = useCallback(async () => {
    if (!user) {
      setConnected(false);
      setChecking(false);
      setCalendars([]);
      return;
    }
    try {
      const resp = await post({ action: "check_status", user_id: user.id });
      if (resp.ok) {
        const data = await resp.json();
        setConnected(!!data.connected);
        setCalendars(data.calendars || []);
      }
    } catch {
      // ignore
    } finally {
      setChecking(false);
    }
  }, [user, post]);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  const connect = useCallback(async (feedUrl: string): Promise<{ success: boolean; calendar_name?: string; error?: string }> => {
    if (!user) return { success: false, error: "not_authenticated" };
    try {
      const resp = await post({ action: "connect", user_id: user.id, feed_url: feedUrl });
      const data = await resp.json();
      if (data.success) {
        await checkStatus();
        return { success: true, calendar_name: data.calendar_name };
      }
      return { success: false, error: data.error || "unknown" };
    } catch {
      return { success: false, error: "network_error" };
    }
  }, [user, post, checkStatus]);

  const disconnect = useCallback(async (feedId?: string) => {
    if (!user) return;
    // Optimistic update: flip the shared state immediately so every consumer
    // (Settings card + Today-tab card) reflects the removal without waiting on
    // the network. Mirrors the Google hook's optimistic disconnect. Without
    // this, a slow/failed reconcile fetch on cellular leaves `connected` stuck
    // true and both surfaces keep showing "connected" (issue #119).
    const remaining = feedId ? calendars.filter((c) => c.id !== feedId) : [];
    setCalendars(remaining);
    setConnected(remaining.length > 0);
    try {
      await post({ action: "disconnect", user_id: user.id, ...(feedId ? { feed_id: feedId } : {}) });
    } catch {
      // ignore
    } finally {
      // Reconcile with server truth (also picks up the real row ids/names).
      await checkStatus();
    }
  }, [user, post, checkStatus, calendars]);

  const fetchEvents = useCallback(async (action: "get_today" | "get_week" | "get_month"): Promise<AppleCalEvent[]> => {
    if (!user) return [];
    try {
      const resp = await post({ action, user_id: user.id });
      if (!resp.ok) return [];
      const data = await resp.json();
      return data.events || [];
    } catch {
      return [];
    }
  }, [user, post]);

  const fetchRange = useCallback(async (startIso: string, endIso: string): Promise<AppleCalEvent[]> => {
    if (!user) return [];
    try {
      const resp = await post({ action: "get_range", user_id: user.id, window_start: startIso, window_end: endIso });
      if (!resp.ok) return [];
      const data = await resp.json();
      return Array.isArray(data.events) ? data.events : [];
    } catch {
      return [];
    }
  }, [user, post]);

  const value = useMemo<AppleCalendarContextValue>(
    () => ({ connected, checking, calendars, checkStatus, connect, disconnect, fetchEvents, fetchRange }),
    [connected, checking, calendars, checkStatus, connect, disconnect, fetchEvents, fetchRange],
  );

  return (
    <AppleCalendarContext.Provider value={value}>
      {children}
    </AppleCalendarContext.Provider>
  );
}

export function useAppleCalendar(): AppleCalendarContextValue {
  const ctx = useContext(AppleCalendarContext);
  if (!ctx) throw new Error("useAppleCalendar must be used within <AppleCalendarProvider>");
  return ctx;
}
